import { useEffect, useState } from 'react';
import { Box, Button, Chip, Link, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { StandaloneAutoCompleteSelect } from '@components/StandaloneAutoCompleteSelect';
import { LabelledSelect } from '@components/StandaloneAutoCompleteSelect/labelled-select';
import {
    DashboardFilterState,
    PERIOD_PRESETS,
    PeriodPreset,
    UNASSIGNED,
    activeFilterCount,
    displayDay,
    extraFilterCount,
    presetPeriod,
} from '../dashboard-filters';
import { DashboardFilterOptions } from '../services/responses/dashboard.response';
import { DateRangeFilter } from './date-range-filter';
import { Segmented, useDashboardColors } from './panel';

interface Props {
    value: DashboardFilterState;
    options?: DashboardFilterOptions;
    onChange: (value: DashboardFilterState) => void;
}

/// O desfecho vai como número, que é como o servidor lê o enum.
const OUTCOMES = [
    { id: '0', key: 'painel.desfechoAberto' },
    { id: '1', key: 'painel.desfechoGanho' },
    { id: '2', key: 'painel.desfechoPerdido' },
];

/// Filtros do topo valem para a tela toda: os de cima aplicam na hora, "Mais filtros" no Aplicar.
export const FilterPanel = ({ value, options, onChange }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = useDashboardColors();

    const [expanded, setExpanded] = useState(false);
    const [draft, setDraft] = useState({ column: value.column, outcome: value.outcome });

    // Fora do painel também se muda filtro; o rascunho acompanha, senão o Aplicar desfaria.
    useEffect(() => setDraft({ column: value.column, outcome: value.outcome }), [value.column, value.outcome]);

    const select = (
        label: string,
        current: string,
        items: { id: string; label: string }[],
        onPick: (id: string) => void,
    ) => (
        // Numa linha só a partir de lg encolhem os selects: o nome comprido já termina em "…".
        <Box sx={{ minWidth: { xs: 180, lg: 120 }, flex: '1 1 150px' }}>
            <LabelledSelect cc={cc} label={label}>
                <StandaloneAutoCompleteSelect
                    options={items.map((item) => ({ value: item.id, label: item.label }))}
                    value={current || null}
                    onChange={(picked) => onPick((picked as string) ?? '')}
                    placeholder={t('painel.todos')}
                    isClearable
                />
            </LabelledSelect>
        </Box>
    );

    const pickPreset = (preset: PeriodPreset) =>
        onChange({ ...value, preset, ...presetPeriod(preset, new Date(), { from: value.from, to: value.to }) });

    const agents = [
        ...(options?.agents ?? []),
        // O vendedor também enxerga os sem dono, como no quadro.
        ...(options ? [{ id: UNASSIGNED, label: t('painel.semResponsavel') }] : []),
    ];

    const labelOf = (items: { id: string; label: string }[] | undefined, id: string) =>
        items?.find((item) => item.id === id)?.label ?? id;

    // Chip só para o que está em "Mais filtros": os selects de cima já mostram a escolha.
    const hidden: { key: keyof DashboardFilterState; label: string }[] = expanded ? [] : [
        value.column && { key: 'column' as const, label: `${t('painel.etapa')}: ${labelOf(options?.columns, value.column)}` },
        value.outcome && {
            key: 'outcome' as const,
            label: `${t('painel.desfecho')}: ${t(OUTCOMES.find((o) => o.id === value.outcome)?.key ?? '')}`,
        },
    ].filter(Boolean) as { key: keyof DashboardFilterState; label: string }[];

    const extra = extraFilterCount(value);

    return (
        <Box
            sx={{
                background: cc.cardBg,
                border: `1px solid ${cc.border}`,
                borderRadius: 3,
                p: 2.5,
                display: 'grid',
                gap: 2,
            }}
        >
            {/* Com as duas datas do Personalizado a linha não cabe: quebra em vez de sair da tela. */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: { xs: 'wrap', lg: value.preset === 'custom' ? 'wrap' : 'nowrap' },
                    alignItems: 'flex-end',
                }}
            >
                {/* Mesmo rótulo e altura dos selects: tamanhos diferentes pareciam telas diferentes. */}
                {/* Não encolhe: os cinco atalhos precisam do texto inteiro. */}
                <Box sx={{ flexShrink: 0 }}>
                    <LabelledSelect cc={cc} label={t('painel.periodo')}>
                        <Segmented
                            value={value.preset}
                            options={PERIOD_PRESETS.map((preset) => ({ value: preset, label: t(`painel.preset.${preset}`) }))}
                            onChange={pickPreset}
                            height={43}
                        />
                    </LabelledSelect>
                </Box>

                {value.preset === 'custom' && (
                    <DateRangeFilter
                        value={{ from: value.from, to: value.to }}
                        onChange={(period) => onChange({ ...value, ...period })}
                        stacked
                    />
                )}

                {select(t('painel.responsavel'), value.owner, agents, (owner) => onChange({ ...value, owner }))}
                {select(t('painel.origem'), value.source, options?.sources ?? [], (source) => onChange({ ...value, source }))}
                {(options?.campaigns.length ?? 0) > 0 && select(
                    t('painel.campanha'),
                    value.campaign,
                    (options?.campaigns ?? []).map((name) => ({ id: name, label: name })),
                    (campaign) => onChange({ ...value, campaign }))}

                <Button
                    variant="outlined"
                    startIcon={<TuneIcon />}
                    onClick={() => setExpanded((open) => !open)}
                    aria-expanded={expanded}
                    sx={{
                        textTransform: 'none',
                        color: cc.accent,
                        borderColor: expanded || extra ? cc.accent : cc.border,
                        background: expanded ? cc.accentSoftBg : 'transparent',
                        height: 43,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        fontWeight: 600,
                        '&:hover': { borderColor: cc.accent, background: cc.accentSoftBg },
                    }}
                >
                    {t('painel.maisFiltros')}{extra ? ` (${extra})` : ''}
                </Button>
            </Box>

            {expanded && (
                <Box sx={{ display: 'grid', gap: 2, pt: 2, borderTop: `1px solid ${cc.divider}` }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {select(t('painel.etapa'), draft.column, options?.columns ?? [],
                            (column) => setDraft((d) => ({ ...d, column })))}
                        {select(t('painel.desfecho'), draft.outcome,
                            OUTCOMES.map((o) => ({ id: o.id, label: t(o.key) })),
                            (outcome) => setDraft((d) => ({ ...d, outcome })))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ color: cc.textMuted, flex: 1 }}>
                            {t('painel.maisFiltrosAjuda')}
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setDraft({ column: value.column, outcome: value.outcome });
                                setExpanded(false);
                            }}
                            sx={{ textTransform: 'none', color: cc.textPrimary, borderColor: cc.border }}
                        >
                            {t('painel.cancelar')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                onChange({ ...value, ...draft });
                                setExpanded(false);
                            }}
                            sx={{ textTransform: 'none', color: '#fff !important', background: cc.accent }}
                        >
                            {t('painel.aplicar')}
                        </Button>
                    </Box>
                </Box>
            )}

            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    pt: 1.5,
                    borderTop: `1px solid ${cc.divider}`,
                }}
            >
                {hidden.map((item) => (
                    <Chip
                        key={item.key}
                        size="small"
                        label={item.label}
                        onDelete={() => onChange({ ...value, [item.key]: '' })}
                        sx={{
                            color: cc.textPrimary,
                            background: cc.accentSoftBg,
                            '& .MuiChip-deleteIcon': { color: cc.textSecondary },
                            colorScheme: controller.darkMode ? 'dark' : 'light',
                        }}
                    />
                ))}
                {activeFilterCount(value) > 1 && (
                    <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={() => onChange({ ...value, owner: '', source: '', campaign: '', column: '', outcome: '' })}
                        sx={{ color: cc.accent, fontSize: '0.75rem', fontWeight: 600 }}
                    >
                        {t('painel.limparFiltros')}
                    </Link>
                )}
                <Typography variant="caption" sx={{ color: cc.textMuted, ml: 'auto' }}>
                    {t('painel.recorte', { de: displayDay(value.from), ate: displayDay(value.to) })}
                </Typography>
            </Box>
        </Box>
    );
};
